import { BALANCE } from '../config/balance';
import { clamp } from '../core/vec';
import { mulberry32 } from '../core/rng';
import type { GameEvent } from '../core/types';

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

/**
 * WebAudio 程序化合成（无素材文件）。
 * 规则见 src/assets/audio-spec.md；逻辑步只发事件，本类在步外 trigger。
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private combo = 0;
  private lastKillAt = -999;

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ctx.destination);
    this.noiseBuffer = this.makeNoise();
  }

  trigger(ev: GameEvent): void {
    if (!this.ctx || !this.master) return;
    switch (ev.type) {
      case 'hit':
        this.hit(ev.amount ?? 0);
        break;
      case 'kill':
        this.kill(ev.amount ?? 0);
        break;
      case 'pickup':
        this.tone('sine', 600, 900, 0.06, 0.12);
        break;
      case 'levelup':
        this.levelup();
        break;
      case 'explosion':
        this.noiseBurst(0.38, 800, 90, 0.32);
        break;
      case 'damage':
        this.tone('square', 200, 110, 0.15, 0.2);
        this.noiseBurst(0.1, 1200, 300, 0.12);
        break;
      case 'heal':
        this.tone('sine', 520, 760, 0.09, 0.14);
        break;
      case 'shield_break':
        this.shieldBreak();
        break;
      case 'death':
        this.tone('sawtooth', 400, 70, 0.8, 0.25);
        this.noiseBurst(0.7, 500, 100, 0.22);
        break;
    }
  }

  private hit(damage: number): void {
    const freq = clamp(320 + damage * 9, 320, 980);
    this.tone('triangle', freq, freq, 0.07, 0.18);
  }

  private kill(damage: number): void {
    const ctx = this.ctx as AudioContext;
    const now = ctx.currentTime;
    if (now - this.lastKillAt < BALANCE.juice.comboWindow) this.combo++;
    else this.combo = 1;
    this.lastKillAt = now;

    const freq = clamp(240 + damage * 6, 240, 900);
    this.tone('square', freq, freq, 0.09, 0.16);
    const layer = Math.min(3, Math.floor(this.combo / 5));
    if (layer >= 1) this.tone('sawtooth', freq, freq, 0.08, 0.08);
    if (layer >= 2) this.tone('triangle', freq * 2, freq * 2, 0.05, 0.08, 0.02);
    if (layer >= 3) this.tone('triangle', freq * 3, freq * 3, 0.05, 0.07, 0.04);
  }

  private levelup(): void {
    const notes = [523.25, 659.25, 783.99];
    notes.forEach((f, i) => this.tone('triangle', f, f, 0.09, 0.16, i * 0.07));
  }

  private shieldBreak(): void {
    this.tone('sine', 1320, 1320, 0.12, 0.1);
    this.tone('sine', 1760, 1760, 0.12, 0.08, 0.01);
  }

  private tone(
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    gain: number,
    delay = 0,
  ): void {
    const ctx = this.ctx as AudioContext;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(this.master as GainNode);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noiseBurst(dur: number, f0: number, f1: number, gain: number): void {
    const ctx = this.ctx as AudioContext;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    const t = ctx.currentTime;
    filter.frequency.setValueAtTime(f0, t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master as GainNode);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  private makeNoise(): AudioBuffer {
    const ctx = this.ctx as AudioContext;
    const len = Math.floor(ctx.sampleRate * 0.5);
    const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    const rng = mulberry32(0x5eed);
    for (let i = 0; i < len; i++) data[i] = rng() * 2 - 1;
    return buffer;
  }
}
